import { useMemo, useState } from 'react';
import Editor from '@monaco-editor/react';
import { Link, useNavigate } from '@tanstack/react-router';
import { CaminhoDoPacoteSchema, LIMITES_TOOLKIT } from '@remoto/protocolo';
import { CriarRotinaSchema, NovaVersaoSchema, pastaCurta, type RotinaDetalhe } from '@remoto/shared';
import { CaixaDeMarcar, CampoDeEscolha } from '../componentes/campos';
import { IconeAnterior, IconeFechar, IconeMais } from '../componentes/icones';
import { linguagemDe } from '../editor/monaco';
import { usarTemaDoEditor } from '../estado/tema';
import { usarSessao } from '../estado/sessao';
import { mensagemDoErro } from '../erros';
import { GESTOS, usarAlvos, usarGesto, usarProjetos } from './consultas';
import { AvisoDeErro, BotaoPrimario, CampoDeLinha, CampoDeTextoLongo } from './pecas';
import {
  ENTRADA_DE_PARTIDA,
  FORMAS,
  lerJson,
  MANIFESTO_DE_PARTIDA,
  manifestoLegivel,
  ROTULO_DA_FORMA,
  validar,
  type FormaDaRotina,
} from './regras';

/**
 * Criar uma rotina ou escrever a versão nova de uma, pela tela (F3.7, D19):
 * um HUMANO criando é proposta já aprovada por ele — a API grava a versão e a
 * decisão juntas. O editor de código é o Monaco servido pelo próprio domínio
 * (`editor/monaco.ts`, CSP), para o manifesto e para cada arquivo do pacote.
 *
 * O corpo é conferido com o MESMO schema da borda da API antes de sair
 * (`validar`): os tetos do pacote, o caminho relativo, a fase `sonda` de
 * leitura. A API confere de novo; aqui é para a pessoa saber QUAL campo, em
 * português, sem uma ida e volta.
 *
 * Voltar atrás não mora aqui: é uma versão nova com o manifesto antigo, e a
 * tela só tem o conteúdo da VIGENTE (as anteriores chegam só com os hashes).
 */

const DA_FROTA = 'frota';

interface ArquivoEmEdicao {
  caminho: string;
  conteudo: string;
}

/** De volta a quem chamou: o detalhe da rotina, ou a lista quando ela ainda não existe. */
function Voltar({ base, className, children }: { base: RotinaDetalhe | null; className: string; children: React.ReactNode }): React.JSX.Element {
  return base ? (
    <Link to="/toolkit/rotinas/$id" params={{ id: base.id }} className={className}>
      {children}
    </Link>
  ) : (
    <Link to="/toolkit" search={{ aba: 'rotinas' }} className={className}>
      {children}
    </Link>
  );
}

export function EditorDaRotina({ base }: { base: RotinaDetalhe | null }): React.JSX.Element {
  const navegar = useNavigate();
  const ehAdmin = usarSessao((e) => e.usuario?.papel) === 'ADMIN';
  const tema = usarTemaDoEditor();
  // Os modelos do Monaco são globais: a chave desta visita impede que um
  // rascunho de antes reapareça no mesmo caminho.
  const [visita] = useState(() => Math.random().toString(36).slice(2, 10));
  const alvos = usarAlvos();
  const projetos = usarProjetos(ehAdmin && base === null);
  const criar = usarGesto(GESTOS.criarRotina);
  const novaVersao = usarGesto(GESTOS.novaVersao);

  const vigente = base?.versoes.find((v) => v.id === base.vigente?.id) ?? null;
  const [nome, setNome] = useState('');
  const [titulo, setTitulo] = useState('');
  const [forma, setForma] = useState<FormaDaRotina>('FUNCAO');
  const [entidade, setEntidade] = useState('');
  const [operacao, setOperacao] = useState('');
  const [projeto, setProjeto] = useState<string>(DA_FROTA);
  const [alvosMarcados, setAlvos] = useState<string[]>([]);
  const [descricao, setDescricao] = useState(vigente?.descricao ?? '');
  const [manifesto, setManifesto] = useState(
    manifestoLegivel(vigente?.manifesto ?? MANIFESTO_DE_PARTIDA),
  );
  const [arquivos, setArquivos] = useState<ArquivoEmEdicao[]>(
    base
      ? base.arquivosDaVigente.map((a) => ({ caminho: a.caminho, conteudo: a.conteudo }))
      : [{ caminho: 'rotina.py', conteudo: ENTRADA_DE_PARTIDA }],
  );
  const [atual, setAtual] = useState(0);
  const [novoCaminho, setNovoCaminho] = useState('');
  const [motivo, setMotivo] = useState('');
  const [problemas, setProblemas] = useState<string[]>([]);

  const json = useMemo(() => lerJson(manifesto), [manifesto]);
  const arquivo = arquivos[Math.min(atual, arquivos.length - 1)];
  const ocupado = criar.isPending || novaVersao.isPending;
  const erro = criar.error ?? novaVersao.error;

  const caminhoNovoValido = CaminhoDoPacoteSchema.safeParse(novoCaminho.trim()).success;
  const caminhoRepetido = arquivos.some((a) => a.caminho === novoCaminho.trim());

  function adicionarArquivo(): void {
    const caminho = novoCaminho.trim();
    if (!caminhoNovoValido || caminhoRepetido) return;
    setArquivos((lista) => [...lista, { caminho, conteudo: '' }]);
    setAtual(arquivos.length);
    setNovoCaminho('');
  }

  function removerArquivo(indice: number): void {
    setArquivos((lista) => lista.filter((_, i) => i !== indice));
    setAtual(0);
  }

  function gravar(): void {
    if (!json.ok) return setProblemas([json.erro]);
    const pacote = { descricao, manifesto: json.valor, arquivos, motivo };
    if (base) {
      const r = validar(NovaVersaoSchema, { ...pacote, ...(base.vigente ? { substituiId: base.vigente.id } : {}) });
      if (!r.ok) return setProblemas(r.problemas);
      setProblemas([]);
      novaVersao.mutate(
        { rotinaId: base.id, corpo: r.dados },
        { onSuccess: () => void navegar({ to: '/toolkit/rotinas/$id', params: { id: base.id } }) },
      );
      return;
    }
    const [maquinaId, cwdProjeto] = projeto === DA_FROTA ? [] : projeto.split('|');
    const r = validar(CriarRotinaSchema, {
      ...pacote,
      nome: nome.trim(),
      titulo,
      forma,
      ...(entidade.trim() ? { entidade } : {}),
      ...(operacao.trim() ? { operacao } : {}),
      ...(maquinaId && cwdProjeto ? { projeto: { maquinaId, cwdProjeto } } : {}),
      alvos: alvosMarcados,
    });
    if (!r.ok) return setProblemas(r.problemas);
    setProblemas([]);
    criar.mutate(r.dados, {
      onSuccess: (criada) => void navegar({ to: '/toolkit/rotinas/$id', params: { id: criada.id } }),
    });
  }

  const opcoesDeAlvo = (alvos.data ?? []).filter((a) => a.ativo);

  return (
    <div className="space-y-4">
      <Voltar base={base} className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200">
        <IconeAnterior tamanho={14} />
        {base ? base.titulo : 'Toolkit'}
      </Voltar>
      <header className="space-y-0.5">
        <h1 className="text-lg font-medium text-zinc-100">
          {base ? `Nova versão de ${base.titulo}` : 'Nova rotina'}
        </h1>
        <p className="text-sm text-zinc-500">
          {base
            ? `Parte da vigente (v${base.vigente?.numero ?? '—'}). Pela tela, a versão nasce aprovada por você e passa a ser a vigente — todo alvo volta a "só ensaio" até um caso real conferido dela.`
            : 'Pela tela, a rotina nasce com a versão 1 aprovada por você, já estendida aos alvos que você marcar (só ensaio até o primeiro caso real).'}
        </p>
      </header>

      {base ? null : (
        <section aria-label="Identidade" className="grid gap-3 sm:grid-cols-2">
          <CampoDeLinha
            rotulo="Nome"
            dica="minúsculas, dígitos e hífen — o nome que as sessões chamam (ex.: djen-consultar)"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            autoCapitalize="off"
            spellCheck={false}
          />
          <CampoDeLinha rotulo="Título" value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={200} />
          <CampoDeEscolha
            rotulo="Forma"
            valor={forma}
            opcoes={FORMAS.map((f) => ({ id: f, rotulo: ROTULO_DA_FORMA[f] }))}
            aoEscolher={setForma}
          />
          {ehAdmin ? (
            <CampoDeEscolha
              rotulo="Onde vale"
              valor={projeto}
              opcoes={[
                { id: DA_FROTA, rotulo: 'toda a frota (todo papel vê)' },
                ...(projetos.data ?? []).map((p) => ({
                  id: `${p.maquinaId}|${p.cwdProjeto}`,
                  rotulo: `${p.maquinaNome} · ${pastaCurta(p.cwdExibicao)}`,
                })),
              ]}
              aoEscolher={setProjeto}
              dica="Rotina de projeto só aparece para quem vê aquele projeto."
            />
          ) : null}
          <CampoDeLinha rotulo="Entidade (opcional)" value={entidade} onChange={(e) => setEntidade(e.target.value)} maxLength={100} />
          <CampoDeLinha rotulo="Operação (opcional)" value={operacao} onChange={(e) => setOperacao(e.target.value)} maxLength={100} />
          <fieldset className="space-y-1.5 sm:col-span-2">
            <legend className="text-sm text-zinc-400">Alvos em que ela vale desde já</legend>
            {opcoesDeAlvo.length === 0 ? (
              <p className="text-xs text-zinc-500">Nenhum alvo ativo cadastrado — cadastre na aba Alvos, ou estenda depois.</p>
            ) : (
              <div className="flex max-h-32 flex-wrap gap-x-4 gap-y-1.5 overflow-y-auto rounded-md border border-zinc-800 p-2">
                {opcoesDeAlvo.map((a) => (
                  <CaixaDeMarcar
                    key={a.id}
                    marcada={alvosMarcados.includes(a.chave)}
                    aoMarcar={(m) => setAlvos((lista) => (m ? [...lista, a.chave] : lista.filter((x) => x !== a.chave)))}
                    className="text-xs text-zinc-300"
                  >
                    <span className="font-mono">{a.chave}</span>
                  </CaixaDeMarcar>
                ))}
              </div>
            )}
          </fieldset>
        </section>
      )}

      <CampoDeTextoLongo
        rotulo="Descrição (markdown — faz parte do hash da versão)"
        value={descricao}
        onChange={(e) => setDescricao(e.target.value)}
        rows={5}
        maxLength={LIMITES_TOOLKIT.descricao}
      />

      <section aria-label="Manifesto" className="space-y-1.5">
        <h2 className="text-sm text-zinc-400">Manifesto (JSON)</h2>
        <div className="h-[min(320px,45vh)] overflow-hidden rounded-md border border-zinc-800" data-testid="editor-do-manifesto">
          <Editor
            value={manifesto}
            path={`toolkit-editor/${visita}/manifesto.json`}
            language="json"
            theme={tema}
            onChange={(v) => setManifesto(v ?? '')}
            options={{
              fontSize: 13,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              wordWrap: 'on',
              tabSize: 2,
              scrollbar: { alwaysConsumeMouseWheel: false },
            }}
          />
        </div>
        {json.ok ? null : <p className="text-xs text-amber-300">{json.erro}</p>}
      </section>

      <section aria-label="Arquivos do pacote" className="space-y-1.5">
        <h2 className="text-sm text-zinc-400">Arquivos do pacote</h2>
        <div role="tablist" aria-label="Arquivos" className="flex min-w-0 flex-wrap gap-1">
          {arquivos.map((a, i) => (
            <span
              key={a.caminho}
              className={
                'inline-flex max-w-full min-w-0 items-center rounded-md border text-xs ' +
                (i === atual ? 'border-sky-700 bg-sky-950/40 text-zinc-100' : 'border-zinc-800 text-zinc-400')
              }
            >
              <button
                type="button"
                role="tab"
                aria-selected={i === atual}
                onClick={() => setAtual(i)}
                className="min-w-0 truncate px-2 py-1 font-mono pointer-coarse:min-h-11"
              >
                {a.caminho}
              </button>
              {arquivos.length > 1 ? (
                <button
                  type="button"
                  onClick={() => removerArquivo(i)}
                  aria-label={'Remover ' + a.caminho}
                  title={'Remover ' + a.caminho}
                  className="px-1.5 py-1 text-zinc-500 hover:text-red-300 pointer-coarse:min-h-11"
                >
                  <IconeFechar tamanho={12} />
                </button>
              ) : null}
            </span>
          ))}
        </div>
        {arquivo ? (
          <div className="h-[min(360px,50vh)] overflow-hidden rounded-md border border-zinc-800" data-testid="editor-do-arquivo">
            <Editor
              value={arquivo.conteudo}
              path={`toolkit-editor/${visita}/arquivos/${arquivo.caminho}`}
              language={linguagemDe(arquivo.caminho)}
              theme={tema}
              onChange={(v) =>
                setArquivos((lista) => lista.map((a) => (a.caminho === arquivo.caminho ? { ...a, conteudo: v ?? '' } : a)))
              }
              options={{
                fontSize: 13,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                automaticLayout: true,
                wordWrap: 'on',
                scrollbar: { alwaysConsumeMouseWheel: false },
              }}
            />
          </div>
        ) : null}
        <div className="flex min-w-0 flex-wrap items-end gap-2">
          <div className="min-w-0 flex-1 basis-48">
            <CampoDeLinha
              rotulo="Novo arquivo"
              placeholder="ex.: lib/comum.py"
              value={novoCaminho}
              onChange={(e) => setNovoCaminho(e.target.value)}
              autoCapitalize="off"
              spellCheck={false}
            />
          </div>
          <button
            type="button"
            onClick={adicionarArquivo}
            disabled={!caminhoNovoValido || caminhoRepetido}
            className="inline-flex items-center gap-1 rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-40 pointer-coarse:min-h-11"
          >
            <IconeMais tamanho={14} />
            Adicionar
          </button>
        </div>
        {novoCaminho.trim() && (!caminhoNovoValido || caminhoRepetido) ? (
          <p className="text-xs text-amber-300">
            {caminhoRepetido ? 'Já existe um arquivo com este caminho.' : 'Caminho relativo com /, sem espaço, sem . nem ..'}
          </p>
        ) : null}
      </section>

      <CampoDeLinha
        rotulo="Motivo"
        dica="Fica no histórico ao lado da versão: por que ela existe."
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        maxLength={LIMITES_TOOLKIT.motivo}
      />

      {problemas.length > 0 ? (
        <AvisoDeErro>
          <span className="block font-medium">Antes de gravar, corrija:</span>
          {problemas.map((p) => (
            <span key={p} className="block">
              {p}
            </span>
          ))}
        </AvisoDeErro>
      ) : null}
      {erro ? <AvisoDeErro>{mensagemDoErro(erro, 'Não foi possível gravar agora.')}</AvisoDeErro> : null}

      {/* Preso ao pé da tela: o botão de gravar não pode depender de rolar
          por dois editores para aparecer. */}
      <div className="sticky bottom-0 -mx-5 flex flex-wrap justify-end gap-2 border-t border-zinc-800 bg-zinc-950/95 px-5 py-3">
        <Voltar
          base={base}
          className="inline-flex items-center rounded-md border border-zinc-700 px-3 py-1.5 text-sm text-zinc-200 transition hover:bg-zinc-800 pointer-coarse:min-h-11"
        >
          Cancelar
        </Voltar>
        <BotaoPrimario disabled={ocupado} onClick={gravar}>
          {base ? 'Gravar versão' : 'Criar rotina'}
        </BotaoPrimario>
      </div>
    </div>
  );
}
